import { useEffect, useRef, useState } from "react";
import api, { API, csrfHeaders } from "@/lib/api";
import { toast } from "sonner";
import { log } from "@/lib/log";

const POLL_MS = 5000;
const MAX_POLLS = 120;

function parseSse(part) {
  let evt = "message", data = "";
  for (const line of part.split("\n")) {
    if (line.startsWith("event:")) evt = line.slice(6).trim();
    if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  return { evt, data };
}

function parseEvent(part) {
  const { evt, data } = parseSse(part);
  if (!data) return null;
  try { return { evt, obj: JSON.parse(data) }; }
  catch (e) { log.warn("Skipping malformed stream event:", e?.message); return null; }
}

// Splits a chunk buffer into complete SSE frames; returns [frames, remainder].
function splitFrames(buf) {
  const parts = buf.split("\n\n");
  return [parts, parts.pop() || ""];
}

async function readFrames(resp, onFrame) {
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) return;
    const [frames, rest] = splitFrames(buf + decoder.decode(value, { stream: true }));
    buf = rest;
    for (const frame of frames) await onFrame(frame);
  }
}

// Polls a run until it completes; used when the SSE connection drops mid-review. Stops when the run is cancelled client-side.
async function pollRun(runId, { complete, appendText }, signal) {
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    if (signal?.aborted) return;
    const { data } = await api.get(`/reviews/${runId}`);
    if (signal?.aborted) return;
    if (data.status === "completed") return complete(data.finding_count, data.summary, runId);
    if (data.status === "cancelled") { toast.info("Review cancelled"); return; }
    if (data.status === "failed") { toast.error(data.error || "Review failed"); return; }
    appendText("\n[still analyzing on server…]");
  }
}

const makeHandlers = ({ complete, appendText }, onStart) => ({
  start: (state, obj) => { state.runId = obj.run_id; onStart?.(obj.run_id); if (obj.reused) { toast.info("A review is already running for this project — attached to it instead of starting another."); appendText("[attached to the review already running on the server…]\n"); } },
  ping: () => {},
  cancelled: (state) => { state.finished = true; toast.info("Review cancelled"); },
  token: (_state, obj) => appendText(obj.t || ""),
  done: async (state, obj) => { state.finished = true; await complete(obj.findings_count, obj.summary, state.runId); },
  error: (state, obj) => { state.finished = true; toast.error(obj.error || "Stream error"); },
});

async function streamRun({ body, signal, state, io, onStart }) {
  const handlers = makeHandlers(io, onStart);
  const resp = await fetch(`${API}/reviews/stream`, { method: "POST", headers: { "Content-Type": "application/json", ...csrfHeaders() }, credentials: "include", body: JSON.stringify(body), signal });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  await readFrames(resp, async (frame) => {
    const ev = parseEvent(frame);
    if (ev && handlers[ev.evt]) await handlers[ev.evt](state, ev.obj);
  });
}

const recover = async (state, reason, io, signal) => { toast.info(reason); await pollRun(state.runId, io, signal); };

// Streams an AI review run over SSE with cookie auth; falls back to polling if the connection drops.
// Token/poll updates are batched (≤ one React commit per FLUSH_MS) so streaming never re-renders the page per token.
const FLUSH_MS = 150;
export function useReviewStream({ projectId, body, onFindings }) {
  const [running, setRunning] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [result, setResult] = useState(null);
  const [runId, setRunIdState] = useState(null);
  const runIdRef = useRef(null);
  const setRunId = (id) => { runIdRef.current = id; setRunIdState(id); };
  const abortRef = useRef(null);
  const pending = useRef("");
  const flushTimer = useRef(null);
  const alive = useRef(true);
  const flush = () => { flushTimer.current = null; if (!alive.current || !pending.current) return; const t = pending.current; pending.current = ""; setStreamText((prev) => prev + t); };
  useEffect(() => () => { alive.current = false; abortRef.current?.abort(); clearTimeout(flushTimer.current); }, []); // leaving the page drops only the client listener; the run continues server-side

  const io = {
    appendText: (t) => { pending.current += t; if (!flushTimer.current) flushTimer.current = setTimeout(flush, FLUSH_MS); },
    complete: async (count, summary, id) => {
      flush();
      if (!alive.current) return;
      setResult({ findings_count: count, summary, run_id: id, mode: body().mode });
      toast.success(`Review complete: ${count} findings`);
      const f = await api.get(`/projects/${projectId}/findings`);
      if (alive.current) onFindings(f.data);
    },
  };

  const runStream = async () => {
    setRunning(true); setStreamText(""); setResult(null); pending.current = "";
    const state = { runId: null, finished: false };
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamRun({ body: body(), signal: controller.signal, state, io, onStart: (id) => { setRunId(id); } });
      if (!state.finished && state.runId && alive.current) await recover(state, "Connection closed — analysis continues on the server, waiting for results…", io, controller.signal);
    } catch (e) {
      if (e.name === "AbortError" || !alive.current) return;
      if (state.runId && !state.finished) await recover(state, "Stream interrupted — waiting for server to finish…", io, controller.signal);
      else toast.error(e.message || "Stream failed");
    } finally { if (alive.current) { setRunning(false); setRunId(null); } abortRef.current = null; }
  };

  // Reattach to a run that is already executing server-side (e.g. user navigated away and came back) instead of starting another.
  const resume = async (id) => {
    setRunning(true); setRunId(id); setResult(null); setStreamText("[reconnected to running review — analysis continues on the server…]\n");
    const controller = new AbortController();
    abortRef.current = controller;
    try { await pollRun(id, io, controller.signal); } finally { if (alive.current) { setRunning(false); setRunId(null); } abortRef.current = null; }
  };

  // Cancel the server-side run (running → cancelled) first, then drop the client listener.
  const cancel = async () => {
    const id = runIdRef.current;
    abortRef.current?.abort(); setRunning(false); setRunId(null);
    if (!id) return;
    try {
      const { data } = await api.post(`/reviews/${id}/cancel`);
      if (alive.current) toast[data.cancelled ? "success" : "info"](data.cancelled ? "Review cancelled on the server" : `Review already ${data.status} — nothing to cancel`);
    } catch (e) { if (alive.current) toast.error(e?.response?.data?.detail || "Could not cancel the review"); }
  };
  return { running, runId, streamText, result, runStream, resume, cancel };
}
